import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import debounce from 'lodash/debounce';

import {
  BodyWrapper,
  Header,
  Loader,
  MainWrapper,
  SidebarWrapper,
} from '@components';
import { GlobalContext } from '@contexts';
import { ChevronRightIcon } from '@svgs';

import { MainSidebar } from '../components/MainSidebar/MainSidebar.jsx';

import { SearchProjects } from './SearchProjects/SearchProjects.jsx';
import { SearchSites } from './SearchSites/SearchSites.jsx';
import { SearchUsers } from './SearchUsers/SearchUsers.jsx';
import './Search.scss';

export const Search = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('websites');
  const [searchDisabled, setSearchDisabled] = useState(false);
  const [searchParamsLoaded, setSearchParamsLoaded] = useState(false);

  useEffect(() => {
    if (!searchParamsLoaded) {
      if (searchParams.get('q')) {
        setSearchTerm(searchParams.get('q'));
      }
      if (searchParams.get('searchType')) {
        setSearchType(searchParams.get('searchType'));
      }
      if (searchParams.get('searchDisabled') === 'true') {
        setSearchDisabled(true);
      }
      setSearchParamsLoaded(true);
    }
  });

  const handleTypeChange = (type) => {
    setSearchType(type);
    setSearchParams({
      ...searchParams,
      searchDisabled: searchDisabled,
      q: searchTerm,
      searchType: type,
    });
  };

  const handleChange = (e) => {
    setSearchTerm(e.target.value);
    setSearchParams({
      ...searchParams,
      searchDisabled: searchDisabled,
      q: e.target.value,
      searchType: searchType,
    });
  };

  const handleDisableChange = () => {
    setSearchDisabled(!searchDisabled);
    setSearchParams({
      ...searchParams,
      searchDisabled: !searchDisabled,
      q: searchTerm,
      searchType: searchType,
    });
  };

  const debouncedResults = useMemo(() => {
    return debounce(handleChange, 300);
  }, []);
  useEffect(() => {
    return () => {
      debouncedResults.cancel();
    };
  });

  return (
    <>
      <Header />
      <BodyWrapper>
        <SidebarWrapper />
        <MainWrapper>
          <div className="sb-main-breadcrumbs">
            <ul className="sb-flex">
              <li className="sb-flex">
                <Link to={`/dashboard/search`}>Search</Link>
                <ChevronRightIcon />
              </li>
            </ul>
          </div>
          <div className="sb-manage-content">
            <h1>Search</h1>
            <div className="sb-search-filters">
              <button
                type="button"
                onClick={() => handleTypeChange('websites')}
                className={
                  `sb-btn ` + (searchType === 'websites' ? 'sb-btn-cancel' : '')
                }
              >
                Websites
              </button>
              <button
                type="button"
                onClick={() => handleTypeChange('projects')}
                className={
                  `sb-btn ` + (searchType === 'projects' ? 'sb-btn-cancel' : '')
                }
              >
                Projects
              </button>
              <button
                type="button"
                onClick={() => handleTypeChange('users')}
                className={
                  `sb-btn ` + (searchType === 'users' ? 'sb-btn-cancel' : '')
                }
              >
                Users
              </button>
            </div>
            <div className="sb-search-form sb-flex">
              <input
                type="search"
                placeholder="Search anything"
                autoComplete="off"
                defaultValue={searchTerm}
                onChange={debouncedResults}
              />
              <label title="Search disabled Sites, Projects, etc.">
                <input
                  type="checkbox"
                  checked={searchDisabled}
                  onChange={handleDisableChange}
                />{' '}
                Search disabled
              </label>
            </div>
            {searchType === 'projects' && searchTerm.length >= 3 && (
              <SearchProjects
                searchTerm={searchTerm}
                searchDisabled={searchDisabled}
              />
            )}
            {searchType === 'users' && searchTerm.length >= 3 && (
              <SearchUsers
                searchTerm={searchTerm}
                searchDisabled={searchDisabled}
              />
            )}
            {searchType === 'websites' && searchTerm.length >= 3 && (
              <SearchSites
                searchTerm={searchTerm}
                searchDisabled={searchDisabled}
              />
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
